import axios from 'axios';
import { reportWarning } from './error-logging';
import { getRoutingKey } from './routing-key';
import { shouldRedirectToLoginForResponse } from './session-auth-http';
import { isRedirectingToLogin, redirectToLoginOnce } from './session-auth-redirect';
import { clearNetworkErrorSnackbarLock, dispatchNetworkErrorSnackbar, isCanceledRequest } from './transport-error';

const CONSECUTIVE_DIGITS = /[\d]{2,}/gi;
const CSRF_COOKIE_NAME = 'studios_csrftoken';
const CSRF_HEADER_NAME = 'X-CSRFToken';
const CSRF_TOKEN_PATTERN = /^[A-Za-z0-9]{32,64}$/;
const CSRF_SAFE_METHODS = new Set(['get', 'head', 'options', 'trace']);

export function pickStudiosCsrfTokenFromCookieString(cookieString) {
  if (!cookieString) {
    return undefined;
  }

  const prefix = `${CSRF_COOKIE_NAME}=`;

  return cookieString
    .split(';')
    .map(part => part.trim())
    .filter(part => part.startsWith(prefix))
    .map(part => decodeURIComponent(part.slice(prefix.length)))
    .find(value => CSRF_TOKEN_PATTERN.test(value));
}

function getStudiosCsrfToken() {
  if (typeof document === 'undefined') {
    return undefined;
  }

  return pickStudiosCsrfTokenFromCookieString(document.cookie);
}

export const httpClient = axios.create({
  baseURL: '/',
});

httpClient.interceptors.request.use(config => {
  if (config.url?.startsWith('/api/')) {
    config.headers['X-CP-Routing-Key'] = getRoutingKey();
  }

  const method = (config.method || 'get').toLowerCase();
  if (!CSRF_SAFE_METHODS.has(method)) {
    const token = getStudiosCsrfToken();
    if (token) {
      config.headers[CSRF_HEADER_NAME] = token;
    }
  }

  return config;
});

let hasInternalError = false;
let failedRequests = [];
const NO_HTTP_RESPONSE_STATUS_CODE = 0;

function clearInternalErrorState() {
  hasInternalError = false;
  failedRequests = [];
}

httpClient.interceptors.response.use(
  response => {
    hasInternalError = false;
    failedRequests = [];
    clearNetworkErrorSnackbarLock();
    return response;
  },
  error => {
    const { response, config = {} } = error;
    const {
      url = '',
      method,
      logErrorToDatadog: shouldLogErrorToDatadog = () => true,
      ignoreInternalError = false,
    } = config;
    let statusString = null;

    if (response) {
      const { status } = response;
      statusString = status.toString();

      if (statusString[0] === '5' || statusString[0] === '4') {
        const normalizedUrl = url.replace(CONSECUTIVE_DIGITS, 'xxx');

        if (shouldLogErrorToDatadog(status)) {
          reportWarning(`${statusString} ${method.toUpperCase()} ${normalizedUrl}`, { url });
        }
      }

      // Session expired or forbidden on partner session APIs → login (see session-auth-http.js).
      // Note: We also have saga middleware, but nearly no Saga re-throws the error, so it rarely triggers there.
      if (shouldRedirectToLoginForResponse(config, status)) {
        clearInternalErrorState();
        redirectToLoginOnce(status);
      }
    }

    if (!ignoreInternalError && !isCanceledRequest(error) && !isRedirectingToLogin()) {
      if (response && statusString[0] === '5') {
        hasInternalError = true;
        failedRequests.push({
          apiEndpoint: url,
          statusCode: parseInt(statusString, 10),
          method: method?.toUpperCase(),
        });
      } else if (!response) {
        failedRequests.push({
          apiEndpoint: url,
          statusCode: NO_HTTP_RESPONSE_STATUS_CODE,
          method: method?.toUpperCase(),
        });
        dispatchNetworkErrorSnackbar();
      }
    }

    return Promise.reject(error);
  }
);

export function getLastErrorStatus() {
  return hasInternalError;
}

export function getFailedRequests() {
  return [...failedRequests];
}

export function getLastFailedRequest() {
  return failedRequests.length ? failedRequests[failedRequests.length - 1] : null;
}
